import { useEffect, useState } from "react";
import { useUnidades } from "../../hooks/test/useUnidades";
import useCeo from "../../hooks/useCEO";
import styles from "../../assets/css/CheckListCamaras.module.css";
import { Spinner } from "../../components/Spinner";
import Paginacion from "../../components/customComponents/Paginacion";
import Swal from "sweetalert2";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faFileCsv, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import { HandleDownload } from "../../components/customComponents/Downloads/HandleDownload";
import moment from "moment";
import "moment/locale/es-mx";
import {generateChecklistPDF} from "../../components/customComponents/Downloads/ChecklistReportMDVRS";

/**
 * CheckListCamaras
 * ----------------
 * Muestra el checklist histórico de cámaras por MDVR en formato de timeline.
 * 
 * Funcionalidades:
 * - Carga clientes y dispositivos desde el backend
 * - Permite filtrar por cliente y MDVR (búsqueda)
 * - Agrupa la información por MDVR
 * - Muestra una línea de tiempo con el estatus de cada dispositivo
 * - Incluye paginación y control de carga (loading)
 */
export const CheckListCamaras = () => {
    /* CONFIGURACIÓN DE SEO / METADATA */
    useCeo({
        title: "CHECK-LIST DE CAMARAS",
        description: "Página de gestión de camaras",
    });
    // getCheckListDevice obtiene la información del backend
    const { getMdvrsDispositivos, getMdvrsClientes, getCheckListDevice, postDownloadEvidence } = useUnidades();

    /* ESTADOS DEL COMPONENTE */
    const [data, setData] = useState([]); // data: almacena el arreglo "resultado"
    const [loading, setLoading] = useState(true); // loading: controla el estado de carga
    const [paginado, setPaginado] = useState([]); // almacena el control del paginado
    const [clientesMdvrs, setClientesMdvrs] = useState([]); // almacenamiento de clientes
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null); // almacena los valores de los clientes seleccionados
    const [cliente, setCliente] = useState(""); // almacena el valor del cliente
    const [palabra, setPalabra] = useState(""); // almacena la busqueda del usuario
    const [fechaInicio, setFechaInicio] = useState(""); // almacena la fecha de inicio de consulta seleccionada por el usuario
    const [fechaFin, setFechaFin] = useState(""); // almacena la fecha final de consulta por el usuario

    useEffect(() => {
        // Carga inicial:
        // - Clientes para el filtro
        // - Primera página del checklist
        loadClients();
        loadInfo(1);
    }, []);

    useEffect(() => {
        // Efecto de búsqueda con debounce
        // Se ejecuta cuando cambia el cliente o la palabra de búsqueda
        // Espera 500ms antes de llamar al backend para evitar múltiples requests
        const delay = setTimeout(() => {
            loadInfo(1);
        }, 500);

        return () => clearTimeout(delay);
    }, [palabra, cliente]);
    /**
     * Obtiene la lista de clientes asociados a MDVRs
     * desde el backend y los almacena para el filtro.
     */
    const loadClients = async () => {
        let resp = await getMdvrsClientes();

        if (resp.ok) {
            setClientesMdvrs(resp.catalogo);
        } else {
            Swal.fire({
                title: resp.msg,
                icon: "info",
                timer: 2500,
                showConfirmButton: false,
            });
        }
    }
    /**
     * Obtiene la información del checklist de dispositivos.
     *
     * @param {number} pagina - Página a consultar en el backend
     *
     * Flujo:
     * 1. Activa loading
     * 2. Consulta dispositivos con filtros (cliente, palabra)
     * 3. Normaliza la información del checklist
     * 4. Actualiza datos y paginación
     * 5. Desactiva loading
     */
    const loadInfo = async (pagina = 1) => {
        setLoading(true);
        try {
            // Se consulta el servicio
            const resp = await getMdvrsDispositivos(pagina, palabra, cliente, 10);
            if (resp.ok) {
                setPaginado({
                    page: resp?.data?.paginacion?.pagina,
                    limit: resp?.data?.paginacion?.totalRegistros,
                    previousPage: resp?.data?.paginacion?.previousPage,
                    nextPage: resp?.data?.paginacion?.nextPage,
                    total: resp?.data?.paginacion?.totalRegistros,
                    totalPaginas: resp?.data?.paginacion?.totalPaginas,
                });

                const dispositivos = resp.data?.dispositivos || [];

                // Normalizar los datos del backend para:
                // - Unificar la estructura
                // - Permitir renderizado sencillo de la timeline
                // - Manejar MDVRs sin checklist (apagados o sin información)
                const normalized = dispositivos.map(d => {
                    // Si el MDVR no tiene checklist:
                    // Se crea un evento "Sin información"
                    if (!d.checklist_dispositivos || d.checklist_dispositivos.length === 0) {
                        return {
                            mdvr: d.mdvr_numero,
                            cliente: d.cliente,
                            flotilla: d.flotilla,
                            fecha: null,
                            estatus_general: "Sin información",
                            gps: false,
                            grabacion: false,
                            responsable: "N/A",
                            apagado: true,
                        };
                    }

                    // Convertimos checklist_dispositivos
                    return d.checklist_dispositivos.map(c => ({
                        mdvr: d.mdvr_numero,
                        cliente: d.cliente,
                        flotilla: d.flotilla,
                        fecha: c.fecha,
                        estatus_general: c.estatus_general.estatus,
                        gps: c.gps,
                        grabacion: c.grabacion,
                        responsable: c.responsable,
                        apagado: !c.encendida,
                        cam1: c.cam1,
                        cam2: c.cam2,
                        cam3: c.cam3,
                        cam4: c.cam4,
                    }));
                });

                // "aplanamos" arrays anidados
                setData(normalized.flat());
            } else {
                setData([]);
                setPaginado({
                    page: 1,
                    limit: 0,
                    previousPage: null,
                    nextPage: null,
                    total: 0,
                    totalPaginas: 0,
                });
                return;
            }
        } catch (error) {
            // Manejo de errores
            console.error("Error cargando checklist", error);
        } finally {
            // Se apaga el loading independientemente del resultado
            setLoading(false);
        }
    }

    /**
     * Agrupa los eventos por MDVR.
     *
     * @param {Array} items - Lista de eventos normalizados
     * @returns {Object} Objeto con MDVR como clave y eventos como valor
     */
    // Convierte:
    // [{ mdvr: "T1", ... }, { mdvr: "T1", ... }]
    // en:
    // { T1: [ {...}, {...} ] }
    const groupByMDVR = (items) =>
        items.reduce((acc, item) => {
            const key = item.mdvr.trim(); // se limpia por seguridad
            if (!acc[key]) acc[key] = [];
            acc[key].push(item);
            return acc;
        }, {});

    /**
     * Ordena los eventos por fecha descendente
     * para mostrar la timeline de derecha a izquierda.
     */
    const sortByDateAsc = (items) =>
        [...items].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

    /**
     * Mapa de colores por estatus del checklist.
     * Se utiliza para:
     * - Badge del MDVR
     * - Puntos de la timeline
     */
    const statusColor = {
        Correcto: "#16a34a",
        Advertencia: "#f59e0b",
        Riesgo: "#dc2626",
        Crítico:"#dc2626",
        "Sin información": "#6b7280",
    };

    /* DATOS AGRUPADOS POR MDVR */
    const grouped = groupByMDVR(data);

    // Manejar el cambio de página
    const handlePageChange = async (newPage) => {
        loadInfo(newPage);
    };

    // Maneja la descarga del checklist en formato CSV
    // Valida que el rango de fechas no exceda 31 días
    const onDownload = async () => {
        let valido = await isRangoValido();
        if (valido)
        HandleDownload(
            fechaInicio,
            fechaFin,
            cliente,
            getCheckListDevice
        );
        else 
         Swal.fire({
            title: "Lo siento!",
            text: "El rango de fechas de consulta máximo permitido es de 31 días",
            icon: "info",
        });
    };

    // Maneja la descarga del checklist en formato PDF
    // Genera el reporte y muestra la evidencia de descarga
    const onDownloadPDF = async () => {
        setLoading(true);
        let valido = await isRangoValido();

        if (!valido) {
            Swal.fire({
                title: "Lo siento!",
                text: "El rango de fechas de consulta máximo permitido es de 31 días",
                icon: "info",
            });
            setLoading(false);
            return;
        }

        await generateChecklistPDF(
            fechaInicio,
            fechaFin,
            cliente,
            getCheckListDevice,
            postDownloadEvidence
        );
        setLoading(false);
    };

    // Valida que el rango de fechas sea válido
    // - La fecha final no puede ser menor a la inicial
    // - El rango máximo permitido es de 31 días
    const isRangoValido = async () => {
        if (!fechaInicio || !fechaFin) return true;

        const inicio = moment(fechaInicio);
        const fin = moment(fechaFin);

        const diferencia = fin.diff(inicio, "days");

        return diferencia >= 0 && diferencia <= 31;
    };


    return (
        <>
            {/* Muestra el spinner mientras la información está cargando */}
            {loading && <Spinner />}
            <div id={styles.filtro}>
                <div className="row justify-content-md-center">
                    {
                        // Renderiza los filtros solo cuando existen clientes disponibles
                        clientesMdvrs?.length >= 1 && (
                            <>
                                <div className="col-3 inputWithIcon">
                                    <input
                                        className="form-control selectCedis"
                                        placeholder="BUSCAR MDVR"
                                        name="palabra"
                                        value={palabra}
                                        onChange={(e) => {
                                            const valor = e.target.value;
                                            setPalabra(valor);
                                        }}
                                    ></input>
                                    <button disabled={true}>
                                        <FontAwesomeIcon id="iconoBuscar" icon={faMagnifyingGlass} />
                                    </button>
                                </div>
                                <div className="col-3">
                                    <Select
                                        options={clientesMdvrs?.map((x) => ({
                                            value: x.cliente,
                                            label: x.cliente,
                                        }))}
                                        className="form-control selectCedis"
                                        classNamePrefix="select"
                                        value={clienteSeleccionado}
                                        onChange={(opcion) => {
                                            setClienteSeleccionado(opcion);
                                            setCliente(opcion?.value || "");
                                        }}
                                        placeholder="Selecciona un cliente"
                                    />
                                </div>
                            </>
                        )
                    }
                    <div className="col-12 col-md-6 d-flex flex-wrap align-items-center gap-2">
                        <div className="col-12 col-sm-auto">
                            <label className="form-control-plaintext">Fecha inicio</label>
                        </div>

                        <div className="col-12 col-sm-auto">
                            <input
                                type="date"
                                className="form-control"
                                value={fechaInicio}
                                onChange={(e) => setFechaInicio(e.target.value)}
                            />
                        </div>

                        <div className="col-12 col-sm-auto">
                            <label className="form-label">Fecha fin</label>
                        </div>

                        <div className="col-12 col-sm-auto">
                            <input
                                type="date"
                                className="form-control"
                                value={fechaFin}
                                min={fechaInicio}
                                max={fechaInicio ? moment(fechaInicio).add(31, "days").format("YYYY-MM-DD") : undefined}
                                onChange={(e) => setFechaFin(e.target.value)}
                            />
                        </div>

                        <div className="col-12 col-sm-1">
                            <button
                                className="btn btn-success w-70"
                                onClick={onDownload}
                                disabled={!fechaInicio || !fechaFin}
                                style={{padding: "12px"}}
                            >
                                <FontAwesomeIcon icon={faFileCsv} style={{fontSize: "18px"}}/>
                            </button>
                        </div>
                        <div className="col-12 col-sm-1">
                            <button
                                className="btn btn-warning w-70"
                                onClick={onDownloadPDF}
                                disabled={!fechaInicio || !fechaFin}
                                style={{padding: "12px"}}
                            >
                                <FontAwesomeIcon icon={faFilePdf} style={{fontSize: "18px"}}/>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.mdvrContainer}>
                {/* Render de la información agrupada por MDVR */}
                {Object.entries(grouped).map(([mdvr, events]) => {
                    // Ordenamos los eventos cronológicamente
                    const sorted = sortByDateAsc(events);
                    // Obtenemos el nombre del cliente 
                    const cliente = events[0].cliente;
                    // Obtenemos el nombre de la flotilla 
                    const flotilla = events[0].flotilla;

                    // Se obtiene el último evento como estatus actual del MDVR
                    const lastStatus = sorted[0].estatus_general;

                    return (
                        <div key={mdvr} className={`${styles.mdvrBlock} row`}>
                            {/* Título del MDVR + estatus actual */}
                            <div className={styles.timelineContainer}>

                                {/* TITULOS FIJOS */}
                                <div className={styles.timelineHeader}>
                                    <h4 className={styles.mdvrTitle}>{cliente}</h4>
                                    <h4 className={styles.mdvrTitle}>
                                        MDVR: {mdvr}
                                        <span
                                            className={styles.badge}
                                            style={{ background: statusColor[lastStatus] || "#6b7280" }}
                                        >
                                            {lastStatus}
                                        </span>
                                    </h4>
                                    <h4 className={styles.mdvrTitle}>{flotilla}</h4>
                                </div>
                                {/* Timeline horizontal */}
                                <div className={styles.timelineWrapper}>
                                    <div className={styles.timeline}>
                                        {sorted.map((item, index) => (
                                            <div key={`${item.mdvr}-${item.fecha}-${index}`} className={styles.timelineItem}>
                                                {/* Punto de la timeline */}
                                                <span
                                                    className={styles.dot}
                                                    style={{
                                                        background: statusColor[item.estatus_general],
                                                    }}
                                                />

                                                {/* Card de detalle */}
                                                <div className={styles.content}>
                                                    <strong>{item.fecha ? moment(item.fecha).format("lll") || "N/A": "N/A"}</strong>
                                                    {
                                                        item.estatus_general === "Sin información" ? (
                                                            <>
                                                                <p>
                                                                    Estatus: {item.estatus_general} |
                                                                    GPS: {item.gps ? "✅" : "❌"} | Grabación:{" "}
                                                                    {item.grabacion ? "✅" : "❌"}
                                                                </p>
                                                                <p>MDVR: Apagado</p>
                                                                <small>Responsable: {item.responsable}</small>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <p>
                                                                    Estatus: {item.estatus_general} |
                                                                    GPS: {item.gps ? "✅" : "❌"} | Grabación:{" "}
                                                                    {item.grabacion ? "✅" : "❌"}
                                                                </p>
                                                                <p>
                                                                    Cámaras: {item.cam1} | {item.cam2} | {item.cam3} |{" "}
                                                                    {item.cam4}
                                                                </p>
                                                                <small>Responsable: {item.responsable}</small>
                                                            </>
                                                        )
                                                    }
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
                <div
                    className="row"
                    style={{
                        position: "fixed",
                        top: "90%",
                        left: "26%",
                        background: "rgb(163 160 160 / 20%)",
                        border: "none",
                        borderTop: "none !important",
                        // box-shadow: 0 -16px 36px rgb(140 137 137 / 20%);
                        boxShadow: "0 -16px 36px rgb(140 137 137 / 20%)",
                        borderRadius: "5px",
                        paddingBottom: "15px"
                    }}
                >
                    {/* 
                        La paginación se muestra solo cuando:
                        - No hay carga activa
                        - Existen resultados
                    */}
                    {!loading && (
                        <>
                            <div className="col align-self-center">

                            </div>
                            <Paginacion paginado={paginado} onPageChange={handlePageChange} />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};
